import { useState, useCallback, useMemo, useEffect } from "react";
import { Button, Spinner, Text } from "@ttc3k/trekker";
import { Center, Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { useQuery, gql } from "@apollo/client";
import { InvoiceCard, OrderCard, OrderDetailModal } from "shared/components";
import { getMultilingualString, isEftPendingChargeId } from "shared/utils";
import { format } from "date-fns";
import type { UnpaidInvoiceRow } from "../../types/unpaidInvoice";
import { PAYMENT_REQUESTS_FOR_TEAM_PURCHASES } from "../../queries/paymentRequestsForTeamPurchases";

const ORDERS_FOR_CURRENT_USER = gql`
  query OrdersForCurrentUser($teamOnly: Boolean!, $limit: Int, $skip: Int) {
    ordersForCurrentUser(teamOnly: $teamOnly, limit: $limit, skip: $skip) {
      _id
      cancelled
      createdDateISO
      stripeMetadata {
        chargeId
      }
      purchasedProducts {
        product {
          _id
          name {
            en
            fr
            es
          }
          type
        }
      }
      customer {
        _id
        firstName
        lastName
      }
    }
  }
`;

type PurchaseTab = "PERSONAL" | "TEAM";

type HomePurchasesContentProps = {
  unpaidInvoices?: UnpaidInvoiceRow[];
  focusedPurchaseTab?: PurchaseTab | null;
  onFocusedPurchaseTabConsumed?: () => void;
};

type PurchaseListItem =
  | {
      kind: "invoice";
      sortDate: number;
      invoice: UnpaidInvoiceRow;
    }
  | {
      kind: "order";
      sortDate: number;
      order: any;
    };

export const HomePurchasesContent = ({
  unpaidInvoices = [],
  focusedPurchaseTab = null,
  onFocusedPurchaseTabConsumed,
}: HomePurchasesContentProps) => {
  const { t } = useTranslation("core", { keyPrefix: "HOME_PAGE.MY_PURCHASES" });
  const [activeTab, setActiveTab] = useState<PurchaseTab>("PERSONAL");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  useEffect(() => {
    if (!focusedPurchaseTab) return;
    setActiveTab(focusedPurchaseTab);
    onFocusedPurchaseTabConsumed?.();
  }, [focusedPurchaseTab, onFocusedPurchaseTabConsumed]);

  const { data, loading: ordersLoading } = useQuery(ORDERS_FOR_CURRENT_USER, {
    variables: {
      teamOnly: activeTab === "TEAM",
      limit: 50,
      skip: 0,
    },
  });

  const { data: teamInvoicesData, loading: teamInvoicesLoading } = useQuery(
    PAYMENT_REQUESTS_FOR_TEAM_PURCHASES,
    {
      skip: activeTab !== "TEAM",
      fetchPolicy: "cache-and-network",
    },
  );

  const orders = data?.ordersForCurrentUser ?? [];
  const teamInvoices = (teamInvoicesData?.paymentRequestsForTeamPurchases ??
    []) as UnpaidInvoiceRow[];

  const handleOrderClick = useCallback((orderId: string) => {
    setSelectedOrderId(orderId);
  }, []);

  const handleCloseOrderDetail = useCallback(() => {
    setSelectedOrderId(null);
  }, []);

  const handleInvoiceClick = useCallback((invoiceNumber: string) => {
    window.open(`/invoice/${invoiceNumber}`, "_blank");
  }, []);

  const personalUnpaidInvoices =
    activeTab === "PERSONAL" ? unpaidInvoices : [];

  const purchaseItems = useMemo(() => {
    const items: PurchaseListItem[] = [];

    if (activeTab === "PERSONAL") {
      for (const invoice of personalUnpaidInvoices) {
        if (!invoice.invoiceNumber) continue;
        const sortDate = invoice.sentAt
          ? new Date(invoice.sentAt).getTime()
          : invoice.createdDateISO
            ? new Date(invoice.createdDateISO).getTime()
            : 0;
        items.push({ kind: "invoice", sortDate, invoice });
      }
    } else {
      for (const invoice of teamInvoices) {
        if (!invoice.invoiceNumber) continue;
        const sortDate = invoice.sentAt
          ? new Date(invoice.sentAt).getTime()
          : invoice.createdDateISO
            ? new Date(invoice.createdDateISO).getTime()
            : 0;
        items.push({ kind: "invoice", sortDate, invoice });
      }
    }

    for (const order of orders) {
      if (!order) continue;
      const sortDate = order.createdDateISO
        ? new Date(order.createdDateISO).getTime()
        : 0;
      items.push({ kind: "order", sortDate, order });
    }

    return items.sort((a, b) => b.sortDate - a.sortDate);
  }, [activeTab, personalUnpaidInvoices, teamInvoices, orders]);

  const loading =
    ordersLoading || (activeTab === "TEAM" && teamInvoicesLoading);

  const hasContent = purchaseItems.length > 0;

  return (
    <>
    <OrderDetailModal
      orderId={selectedOrderId}
      open={!!selectedOrderId}
      onClose={handleCloseOrderDetail}
    />
    <Flex flexDir="column" gap="400">
      <Flex gap="200">
        <Button
          size="sm"
          visual={activeTab === "PERSONAL" ? "primary" : "unstyled"}
          mode={activeTab === "PERSONAL" ? "dark" : "light"}
          onClick={() => setActiveTab("PERSONAL")}
        >
          {t("PERSONAL_TAB")}
        </Button>
        <Button
          size="sm"
          visual={activeTab === "TEAM" ? "primary" : "unstyled"}
          mode={activeTab === "TEAM" ? "dark" : "light"}
          onClick={() => setActiveTab("TEAM")}
        >
          {t("TEAM_TAB")}
        </Button>
      </Flex>

      <Text visual="smallRegular" color="text.mid">
        {activeTab === "PERSONAL"
          ? t("PERSONAL_DESCRIPTION")
          : t("TEAM_DESCRIPTION")}
      </Text>

      {loading && !hasContent ? (
        <Center py="600">
          <Spinner w="38px" h="38px" />
        </Center>
      ) : !hasContent ? (
        <Center py="600">
          <Text visual="bodyRegular" color="text.light">
            {t("NO_PURCHASES")}
          </Text>
        </Center>
      ) : (
        <Flex flexDir="column" gap="300">
          {purchaseItems.map(item => {
            if (item.kind === "invoice") {
              const invoice = item.invoice;
              const isOverdue = invoice.status === "OVERDUE";
              const dueDateStr = invoice.dueDate
                ? t("DUE_DATE", {
                    date: format(new Date(invoice.dueDate), "MMM d, yyyy"),
                  })
                : undefined;
              const title = invoice.productTitle || t("INVOICE_FALLBACK_TITLE");

              return (
                <InvoiceCard
                  key={invoice._id}
                  invoiceNumber={invoice.invoiceNumber!}
                  title={title}
                  amountCents={invoice.grandTotal}
                  dueDateStr={dueDateStr}
                  recipientName={invoice.recipientName ?? undefined}
                  isOverdue={isOverdue}
                  dueLabel={t("INVOICE_DUE")}
                  overdueLabel={t("INVOICE_OVERDUE")}
                  onClick={() => handleInvoiceClick(invoice.invoiceNumber!)}
                />
              );
            }

            const order = item.order;
            const productName = order.purchasedProducts?.[0]?.product?.name
              ? getMultilingualString(order.purchasedProducts[0].product.name)
              : null;
            const title = productName || order._id;
            const dateStr = order.createdDateISO
              ? format(new Date(order.createdDateISO), "MMM d, yyyy h:mm a")
              : "";
            const purchaserName = order.customer
              ? `${order.customer.firstName} ${order.customer.lastName}`
              : undefined;

            const isEftPending = isEftPendingChargeId(
              order.stripeMetadata?.chargeId,
            );

            return (
              <OrderCard
                key={order._id}
                orderId={order._id}
                title={title}
                purchaserName={purchaserName}
                dateStr={dateStr}
                isCancelled={order.cancelled}
                isPending={!order.cancelled && isEftPending}
                cancelledLabel={t("CANCELLED")}
                confirmedLabel={t("CONFIRMED")}
                pendingLabel={t("PENDING")}
                onClick={() => handleOrderClick(order._id)}
              />
            );
          })}
        </Flex>
      )}
    </Flex>
    </>
  );
};
